// ============================================
// event-detail.component.ts
// ============================================

import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  DestroyRef,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as L from 'leaflet';
import {
  AppEvent,
  EVENT_TYPES,
  EventLocation,
  Recommandation,
  WEATHER_ICONS,
} from 'src/app/models/event.model';
import { AuthService } from 'src/app/services/auth.service';
import { PlanFeatureService } from 'src/app/services/plan-feature.service';
import { FEATURES } from 'src/app/shared/config/plan-features';
import { MerchantType } from 'src/app/models/onboarding.model';
import { EventService } from 'src/app/services/event.service';
import { EventRefreshService } from 'src/app/services/event-refresh.service';
import { LocationService } from 'src/app/services/location.service';
import {
  EventWeatherForecast,
  HourlyWeatherSlot,
} from 'src/app/models/location.models';
import { PlanBadgeComponent } from 'src/app/shared/components/feature-lock/plan-badge.component';

@Component({
  selector: 'app-event-detail',
  imports: [CommonModule, ReactiveFormsModule, PlanBadgeComponent],
  templateUrl: './event-details.component.html',
  styleUrls: ['./event-details.component.css'],
})
export class EventDetailsComponent implements OnInit, AfterViewChecked {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  readonly planFeatureService = inject(PlanFeatureService);
  readonly FEATURES = FEATURES;
  private currentEventId: string | null = null;

  event: AppEvent | null = null;
  recommandations: Recommandation[] = [];
  isLoading = true;
  errorMessage = '';
  weatherIcons = WEATHER_ICONS;
  showDeleteConfirm = false;
  recommandationsOpen = false; // accordion closed by default

  // ── Météo live ──
  weatherForecast: Extract<EventWeatherForecast, { available: true }> | null =
    null;
  weatherSlots: HourlyWeatherSlot[] = [];
  weatherConfidence = 0;
  weatherDaysAway = 0;
  isLoadingWeather = false;
  /** true si l'événement est dans la fenêtre de prévision (≤16 jours) */
  weatherAvailable = false;
  weatherImpactText = '';
  weatherImpactPct = 0;

  @ViewChild('routeMapContainer', { static: false })
  routeMapContainer?: ElementRef;
  private routeMap?: L.Map;
  private routeMapInitialized = false;

  private locationService = inject(LocationService);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventService: EventService,
    private authService: AuthService,
    private refreshService: EventRefreshService,
  ) {
    this.destroyRef.onDestroy(() => {
      if (this.routeMap) {
        this.routeMap.remove();
        this.routeMap = undefined;
      }
    });
  }

  ngOnInit(): void {
    const eventId = this.route.snapshot.paramMap.get('id');
    this.currentEventId = eventId;
    if (eventId) {
      this.loadEvent(eventId);
    }

    // Mutation réelle → reload HTTP
    this.refreshService.dataChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.currentEventId) this.loadEvent(this.currentEventId);
      });

    // Franchissement d'heure → mise à jour locale du statut affiché
    this.refreshService.transitions$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.event) {
          this.event = {
            ...this.event,
            status: this.resolveCurrentStatus(this.event),
          };
          this.refreshService.scheduleTransitions([this.event]);
        }
        this.cdr.markForCheck();
      });
  }

  ngAfterViewChecked(): void {
    if (
      !this.routeMapInitialized &&
      !this.isLoading &&
      this.event &&
      this.routeMapContainer?.nativeElement
    ) {
      this.routeMapInitialized = true;
      // setTimeout: évite ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => this.initRouteMap(), 0);
    }
  }

  loadEvent(id: string): void {
    this.isLoading = true;
    this.eventService.getEvent(id).subscribe({
      next: (event) => {
        this.event = event;
        this.refreshService.scheduleTransitions([event]);
        this.loadRecommandations(id);
        this.loadWeatherData(event);
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = 'Événement introuvable';
        this.cdr.markForCheck();
      },
    });
  }

  loadRecommandations(eventId: string): void {
    this.eventService.getRecommandations(eventId).subscribe({
      next: (recommandations) => {
        this.recommandations = recommandations;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.recommandations = [];
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** Calcule le statut réel d'un event selon l'heure courante (sans appel réseau). */
  private resolveCurrentStatus(event: AppEvent): AppEvent['status'] {
    if (
      event.status === 'completed' ||
      event.status === 'analyzed' ||
      event.status === 'cancelled'
    )
      return event.status;

    const now = new Date();
    const eventDay = new Date(event.eventDate);
    eventDay.setHours(0, 0, 0, 0);
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    if (eventDay.getTime() !== today.getTime()) return event.status;

    const startDt = new Date(event.eventDate);
    const st = (event as any).startTime;
    if (st) {
      const [h, m] = st.split(':').map(Number);
      startDt.setHours(h, m, 0, 0);
    } else {
      startDt.setHours(0, 0, 0, 0);
    }

    const endDt = new Date(event.eventDate);
    const et = (event as any).endTime;
    if (et) {
      const [h, m] = et.split(':').map(Number);
      endDt.setHours(h, m, 0, 0);
    } else {
      endDt.setHours(23, 59, 0, 0);
    }

    if (endDt <= now) return 'completed';
    if (startDt <= now) return 'active';
    return 'planned';
  }

  /** Initialise la carte Leaflet en lecture seule avec tracé OSRM */
  private initRouteMap(): void {
    const loc = this.event?.location;
    if (!loc || typeof loc !== 'object') return;
    const evtLoc = loc as EventLocation;
    if (!evtLoc.coordinates?.lat) return;
    if (!this.routeMapContainer?.nativeElement) return;
    if (this.routeMap) return;

    const evtLat = evtLoc.coordinates.lat;
    const evtLng = evtLoc.coordinates.lng;

    this.routeMap = L.map(this.routeMapContainer.nativeElement, {
      scrollWheelZoom: false,
      doubleClickZoom: false,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(this.routeMap);

    const routeData = evtLoc.routeData;

    if (routeData?.geometry?.coordinates?.length) {
      // --- Route polyline ---
      L.geoJSON(routeData.geometry as any, {
        style: { color: '#3b82f6', weight: 5, opacity: 0.75 },
      }).addTo(this.routeMap);

      // --- Base marker 🏠 (premier point de la route) ---
      const first = routeData.geometry.coordinates[0]; // [lng, lat]
      const baseLat = first[1];
      const baseLng = first[0];
      L.marker([baseLat, baseLng], {
        draggable: false,
        icon: L.divIcon({
          html: '<span style="font-size:22px;line-height:1;">&#x1F3E0;</span>',
          className: '',
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        }),
      }).addTo(this.routeMap);

      // --- Event marker 🔵 (dernier point / coordonnées de l'événement) ---
      L.marker([evtLat, evtLng], {
        draggable: false,
        icon: L.divIcon({
          html: '<span style="font-size:22px;line-height:1;">&#x1F535;</span>',
          className: '',
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        }),
      }).addTo(this.routeMap);

      // fitBounds sur la route pour tout voir
      const coords = routeData.geometry.coordinates.map(
        (c: number[]) => [c[1], c[0]] as L.LatLngTuple,
      );
      this.routeMap.fitBounds(L.latLngBounds(coords), { padding: [30, 30] });
    } else {
      // Pas de route : centrer juste sur le lieu de l'événement
      this.routeMap.setView([evtLat, evtLng], 13);
      L.marker([evtLat, evtLng], { draggable: false }).addTo(this.routeMap);
    }

    setTimeout(() => this.routeMap?.invalidateSize(), 100);
  }

  get locationCity(): string {
    const loc = this.event?.location;
    if (!loc) return '';
    if (typeof loc === 'object') return loc.city || loc.address || '';
    return loc;
  }

  get locationAddress(): string {
    const loc = this.event?.location;
    if (!loc || typeof loc !== 'object') return '';
    return loc.address || '';
  }

  /** Données de trajet OSRM sauvegardées en DB */
  get locationRouteData(): EventLocation['routeData'] | null {
    const loc = this.event?.location;
    if (!loc || typeof loc !== 'object') return null;
    return (loc as EventLocation).routeData ?? null;
  }

  /** Formate une durée en minutes → "Xh YYmin" ou "YY min" */
  formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m.toString().padStart(2, '0')}min` : `${h}h`;
  }

  getEventTypeDetails(type: string) {
    return EVENT_TYPES.find((t) => t.value === type);
  }

  getWeatherIcon(condition?: string): string {
    if (!condition) return '☁️';
    return this.weatherIcons[condition as keyof typeof WEATHER_ICONS] || '☁️';
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(date));
  }

  exportToPrint(): void {
    if (!this.planFeatureService.canAccess(FEATURES.EXPORT_PDF)) {
      return; // Bloqué par le plan — le bouton affiche un badge
    }
    window.print();
  }

  toggleRecommandations(): void {
    this.recommandationsOpen = !this.recommandationsOpen;
  }

  duplicateEvent(): void {
    if (!this.event) return;
    const e = this.event;
    const data: Record<string, any> = {
      name: e.name,
      eventType: e.eventType,
      startTime: e.startTime,
      endTime: e.endTime,
    };
    if (e.expectedAttendance) data['expectedAttendance'] = e.expectedAttendance;
    if (e.notes) data['notes'] = e.notes;
    if (e.location && typeof e.location === 'object') {
      const loc = e.location as any;
      data['location'] = {
        address: loc.address || '',
        city: loc.city || '',
        coordinates: loc.coordinates || null,
      };
    }
    sessionStorage.setItem('duplicateEvent', JSON.stringify(data));
    this.router.navigate(['/events/create']);
  }

  isEventPast(): boolean {
    if (!this.event) return false;
    if (this.event.status === 'completed' || this.event.status === 'analyzed')
      return true;
    if (this.event.status !== 'planned' && this.event.status !== 'active')
      return false;

    const now = new Date();
    const eventDay = new Date(this.event.eventDate);
    eventDay.setHours(0, 0, 0, 0);
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    // Future day → not past
    if (eventDay > today) return false;

    // Past day → past
    if (eventDay < today) return true;

    // Same day → check endTime
    if (this.event.endTime) {
      const [h, m] = this.event.endTime.split(':').map(Number);
      const endDt = new Date(eventDay);
      endDt.setHours(h, m, 0, 0);
      return now >= endDt;
    }

    return false;
  }

  editEvent(): void {
    if (this.event) {
      this.router.navigate(['/events', this.event.id, 'edit']);
    }
  }

  deleteEvent(): void {
    if (this.event) {
      this.eventService.deleteEvent(this.event.id).subscribe({
        next: () => {
          this.router.navigate(['/events']);
        },
        error: (error) => {
          alert('Erreur lors de la suppression');
        },
      });
    }
  }

  goToSalesInput(): void {
    if (!this.event) return;
    const user = this.authService.getCurrentUser();
    const isItinerant = user?.merchantType === MerchantType.ITINERANT;
    if (isItinerant) {
      this.router.navigate(['/sales/entry-itinerant'], {
        queryParams: { eventId: this.event.id },
      });
    } else {
      this.router.navigate(['/sales/entry-fixed']);
    }
  }

  // ── Météo live ──────────────────────────────────────────────

  private loadWeatherData(event: AppEvent): void {
    // Only for future/today events with coordinates
    if (
      event.status === 'completed' ||
      event.status === 'analyzed' ||
      event.status === 'cancelled'
    )
      return;
    const loc = event.location;
    if (!loc || typeof loc !== 'object' || !loc.coordinates?.lat) return;

    const eventDate = new Date(event.eventDate);
    eventDate.setHours(12, 0, 0, 0);
    const now = new Date();
    const diffMs = eventDate.getTime() - now.getTime();
    const daysAway = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    this.weatherDaysAway = Math.max(0, daysAway);

    // Open-Meteo max 16 days
    if (daysAway > 16) return;

    this.isLoadingWeather = true;
    const dateStr = eventDate.toISOString().split('T')[0];
    const lat = loc.coordinates.lat;
    const lng = loc.coordinates.lng;

    this.locationService.getWeatherForDate(lat, lng, dateStr).subscribe({
      next: (forecast) => {
        if (forecast.available) {
          this.weatherForecast = forecast;
          this.weatherAvailable = true;
          this.computeWeatherMeta(forecast, daysAway);

          // Hourly slots: available for today only
          const todayStr = new Date().toISOString().split('T')[0];
          if (dateStr === todayStr || daysAway <= 1) {
            this.locationService
              .getWeatherSlots(
                lat,
                lng,
                dateStr,
                event.startTime,
                event.endTime,
              )
              .subscribe({
                next: (resp) => {
                  if (resp.available && resp.slots) {
                    this.weatherSlots = resp.slots;
                  }
                  this.isLoadingWeather = false;
                  this.cdr.markForCheck();
                },
                error: () => {
                  this.isLoadingWeather = false;
                  this.cdr.markForCheck();
                },
              });
          } else {
            this.isLoadingWeather = false;
          }
        } else {
          this.isLoadingWeather = false;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoadingWeather = false;
        this.cdr.markForCheck();
      },
    });
  }

  private computeWeatherMeta(
    f: Extract<EventWeatherForecast, { available: true }>,
    daysAway: number,
  ): void {
    // Confidence based on forecast horizon
    const hoursAway = daysAway * 24;
    this.weatherConfidence =
      hoursAway < 24
        ? 95
        : hoursAway < 48
          ? 88
          : hoursAway < 72
            ? 78
            : hoursAway < 168
              ? 65
              : 45;

    // Impact estimation
    let impactPct = 0;
    let impactParts: string[] = [];

    if (f.precipitationSum > 15) {
      impactPct -= 25;
      impactParts.push(
        `pluie forte (${Math.round(f.precipitationSum)}mm) → -25%`,
      );
    } else if (f.precipitationSum > 5) {
      impactPct -= 15;
      impactParts.push(`pluie (${Math.round(f.precipitationSum)}mm) → -15%`);
    }

    if (f.temperatureMin < 5) {
      impactPct -= 12;
      impactParts.push(`froid (${Math.round(f.temperatureMin)}°C) → -12%`);
    }

    if (f.temperatureMax > 35) {
      impactPct -= 5;
      impactParts.push(`chaleur (${Math.round(f.temperatureMax)}°C) → -5%`);
    }

    if (impactParts.length === 0) {
      impactParts.push("conditions favorables → pas d'impact négatif");
    }

    this.weatherImpactPct = impactPct;
    this.weatherImpactText = impactParts.join(' · ');
  }

  /** Label confiance coloré */
  get confidenceLabel(): string {
    if (this.weatherConfidence >= 88) return 'Très fiable';
    if (this.weatherConfidence >= 75) return 'Fiable';
    if (this.weatherConfidence >= 60) return 'Modérée';
    return 'Indicative';
  }

  get confidenceColor(): string {
    if (this.weatherConfidence >= 88)
      return 'text-green-600 dark:text-green-400';
    if (this.weatherConfidence >= 75) return 'text-blue-600 dark:text-blue-400';
    if (this.weatherConfidence >= 60)
      return 'text-amber-600 dark:text-amber-400';
    return 'text-gray-500 dark:text-gray-400';
  }

  get confidenceBgColor(): string {
    if (this.weatherConfidence >= 88)
      return 'bg-green-100 dark:bg-green-900/30';
    if (this.weatherConfidence >= 75) return 'bg-blue-100 dark:bg-blue-900/30';
    if (this.weatherConfidence >= 60)
      return 'bg-amber-100 dark:bg-amber-900/30';
    return 'bg-gray-100 dark:bg-slate-700';
  }

  /** Icône météo depuis weatherCode Open-Meteo */
  getWeatherCodeIcon(code: number): string {
    if (code <= 1) return '☀️';
    if (code <= 3) return '⛅';
    if (code <= 48) return '☁️';
    if (code <= 57) return '🌦️';
    if (code <= 67) return '🌧️';
    if (code <= 77) return '❄️';
    if (code <= 82) return '🌧️';
    if (code <= 86) return '❄️';
    return '⛈️';
  }

  /** Couleur de fond d'un slot horaire selon les conditions */
  getSlotBgClass(slot: HourlyWeatherSlot): string {
    if (slot.precipitationProbability > 60)
      return 'bg-blue-50 dark:bg-blue-900/20';
    if (slot.precipitationProbability > 30)
      return 'bg-amber-50 dark:bg-amber-900/20';
    if (slot.temperature > 35) return 'bg-red-50 dark:bg-red-900/20';
    if (slot.temperature < 5) return 'bg-cyan-50 dark:bg-cyan-900/20';
    return 'bg-green-50 dark:bg-green-900/20';
  }

  goBack(): void {
    this.router.navigate(['/events']);
  }
}
